import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation, Trans } from "react-i18next";
import { Check, X } from "lucide-react";
import { AppShell } from "@/components/AppShell";
import { useBillingStatus } from "@/lib/queries";
import { StripeEmbeddedCheckout } from "@/components/StripeEmbeddedCheckout";
import { PaymentTestModeBanner } from "@/components/PaymentTestModeBanner";
import { hasPaymentsConfigured } from "@/lib/stripe";
import { useQueryClient, useMutation } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { undoDowngrade } from "@/lib/billing.functions";
import { getStripeEnvironment } from "@/lib/stripe";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/plans")({
  head: () => ({
    meta: [{ title: "Plans — VISIKT" }, { name: "robots", content: "noindex" }],
  }),
  component: PlansPage,
});

function PlansPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [yearly, setYearly] = useState(true);
  const [checkoutPriceId, setCheckoutPriceId] = useState<string | null>(null);
  const [ackWithdrawal, setAckWithdrawal] = useState(false);
  const [ackError, setAckError] = useState(false);
  const billing = useBillingStatus();
  const isPro = billing.data?.plan === "pro";
  const scheduledCancel = isPro && billing.data?.cancelAtPeriodEnd;
  const paymentsReady = hasPaymentsConfigured();
  const undoFn = useServerFn(undoDowngrade);

  const undoMutation = useMutation({
    mutationFn: async () => {
      const res = await undoFn({ data: { environment: getStripeEnvironment() } });
      if ("error" in res) throw new Error(res.error);
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["billing-status"] });
      toast.success(t("profile.undoDowngradeToast"));
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const proCtaLabel = scheduledCancel
    ? t("plans.keepPro")
    : isPro
      ? t("plans.currentPlan")
      : yearly
        ? t("plans.startProYearly")
        : t("plans.startProMonthly");

  async function handleProCta() {
    if (!paymentsReady) return;
    if (scheduledCancel) {
      undoMutation.mutate();
      return;
    }
    if (isPro) return;
    if (!ackWithdrawal) {
      setAckError(true);
      return;
    }
    const priceId = yearly ? "pro_yearly" : "pro_monthly";
    try {
      const { data: u } = await supabase.auth.getUser();
      if (u.user) {
        await supabase.from("checkout_consents").insert({
          user_id: u.user.id,
          plan: "pro",
          billing_cycle: yearly ? "yearly" : "monthly",
          price_id: priceId,
          locale: i18n.language ?? null,
          withdrawal_ack: true,
          user_agent: typeof navigator !== "undefined" ? navigator.userAgent.slice(0, 500) : null,
        });
      }
    } catch (e) {
      console.error("checkout_consents insert failed", e);
    }
    setCheckoutPriceId(priceId);
  }

  return (
    <AppShell>
      <section className="animate-entry">
        <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-3">
          {t("plans.eyebrow")}
        </p>
        <h1 className="text-3xl font-display font-bold tracking-tight leading-[1.1]">
          {t("plans.headline")}
        </h1>
        <p className="mt-3 text-sm text-muted-foreground max-w-prose">{t("plans.body")}</p>
      </section>

      <section>
        <div className="flex items-center gap-1 w-fit font-mono text-[10px] uppercase tracking-widest border border-border rounded-full overflow-hidden">
          <button
            type="button"
            onClick={() => setYearly(false)}
            className={
              "px-4 py-2 transition-colors " +
              (!yearly ? "bg-foreground text-background" : "text-muted-foreground")
            }
          >
            {t("pricing.billingToggle.monthly")}
          </button>
          <button
            type="button"
            onClick={() => setYearly(true)}
            className={
              "px-4 py-2 transition-colors flex items-center gap-2 " +
              (yearly ? "bg-foreground text-background" : "text-muted-foreground")
            }
          >
            {t("pricing.billingToggle.yearly")}
            <span
              className={
                "text-[9px] rounded px-1.5 py-0.5 " +
                (yearly ? "bg-background/20" : "bg-foreground/10 text-foreground")
              }
            >
              {t("pricing.pro.yearlySaving")}
            </span>
          </button>
        </div>
      </section>

      <section className="grid grid-cols-1 gap-4">
        <div className="border border-border rounded-2xl p-6 flex flex-col gap-6">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-2">
              {t("pricing.free.name")}
            </p>
            <div className="flex items-end gap-1">
              <span className="text-4xl font-display font-bold tabular-nums">
                {t("pricing.free.price")}
              </span>
              <span className="text-sm text-muted-foreground mb-1">NOK</span>
            </div>
            <p className="mt-2 text-sm text-muted-foreground">{t("pricing.free.description")}</p>
          </div>
          <ul className="flex-1 space-y-2.5">
            {(t("pricing.free.features", { returnObjects: true }) as string[]).map((f) => (
              <li key={f} className="flex items-start gap-2.5 text-sm">
                <Check className="size-3.5 mt-0.5 shrink-0 text-muted-foreground" />
                <span>{f}</span>
              </li>
            ))}
          </ul>
          <button
            type="button"
            onClick={() => navigate({ to: "/dashboard" })}
            className="block w-full py-3 text-center rounded-md border border-border text-foreground font-semibold text-sm hover:bg-foreground/5 transition-colors"
          >
            {isPro ? t("plans.free.continue") : t("plans.free.continueFree")}
          </button>
        </div>

        <div className="border-2 border-foreground rounded-2xl p-6 flex flex-col gap-6 relative">
          <div className="absolute -top-3 left-6">
            <span className="font-mono text-[9px] uppercase tracking-widest bg-foreground text-background px-2.5 py-1 rounded-full">
              {t("pricing.pro.name")}
            </span>
          </div>
          <div>
            <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-2">
              {t("pricing.pro.name")}
            </p>
            <div className="flex items-end gap-1">
              <span className="text-4xl font-display font-bold tabular-nums">
                {yearly ? t("pricing.pro.priceYearly") : t("pricing.pro.priceMonthly")}
              </span>
              <span className="text-sm text-muted-foreground mb-1">
                NOK {yearly ? t("pricing.pro.perYear") : t("pricing.pro.perMonth")}
              </span>
            </div>
            <p className="mt-2 text-sm text-muted-foreground">{t("pricing.pro.description")}</p>
          </div>
          <ul className="flex-1 space-y-2.5">
            {(t("pricing.pro.features", { returnObjects: true }) as string[]).map((f) => (
              <li key={f} className="flex items-start gap-2.5 text-sm">
                <Check className="size-3.5 mt-0.5 shrink-0 text-foreground" />
                <span>{f}</span>
              </li>
            ))}
          </ul>
          {!isPro && (
            <div className="space-y-2 -mt-2">
              <p className="text-[11px] text-muted-foreground leading-relaxed">
                <Trans
                  i18nKey={yearly ? "plans.renewalInfoYearly" : "plans.renewalInfoMonthly"}
                  values={{
                    price: yearly ? t("pricing.pro.priceYearly") : t("pricing.pro.priceMonthly"),
                  }}
                  components={{
                    tosLink: (
                      <a
                        href="/terms"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline underline-offset-4 text-foreground"
                      />
                    ),
                  }}
                />
              </p>
              <label className="flex items-start gap-2.5 text-xs text-muted-foreground cursor-pointer">
                <input
                  type="checkbox"
                  checked={ackWithdrawal}
                  onChange={(e) => {
                    setAckWithdrawal(e.target.checked);
                    if (e.target.checked) setAckError(false);
                  }}
                  className="mt-0.5 size-4 accent-accent flex-shrink-0"
                  aria-invalid={ackError}
                />
                <span className="leading-relaxed">
                  <Trans
                    i18nKey="plans.withdrawalAck"
                    components={{
                      tosLink: (
                        <a
                          href="/terms"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="underline underline-offset-4 text-foreground"
                        />
                      ),
                    }}
                  />
                </span>
              </label>
              {ackError && !ackWithdrawal && (
                <p className="text-[11px] text-destructive">{t("plans.withdrawalRequired")}</p>
              )}
            </div>
          )}
          <button
            type="button"
            onClick={handleProCta}
            disabled={!paymentsReady || (isPro && !scheduledCancel) || (!isPro && !ackWithdrawal)}
            className={
              "block w-full py-3 text-center rounded-md font-semibold text-sm transition-colors " +
              (!paymentsReady || (isPro && !scheduledCancel) || (!isPro && !ackWithdrawal)
                ? "bg-foreground/40 text-background cursor-not-allowed"
                : "bg-foreground text-background hover:bg-foreground/90")
            }
          >
            {proCtaLabel}
          </button>
          {!paymentsReady && (
            <p className="text-[11px] text-muted-foreground text-center -mt-3">
              {t("plans.checkoutSoon")}
            </p>
          )}
        </div>
      </section>

      {checkoutPriceId && (
        <div className="fixed inset-0 z-50 bg-background/95 backdrop-blur-sm flex flex-col overflow-y-auto">
          <PaymentTestModeBanner />
          <div className="flex justify-end p-4">
            <button
              type="button"
              onClick={() => {
                setCheckoutPriceId(null);
                qc.invalidateQueries({ queryKey: ["billing-status"] });
              }}
              className="rounded-full p-2 hover:bg-foreground/10"
              aria-label="Close"
            >
              <X className="size-5" />
            </button>
          </div>
          <div className="max-w-2xl w-full mx-auto px-4 pb-16">
            <StripeEmbeddedCheckout
              priceId={checkoutPriceId}
              returnUrl={`${window.location.origin}/checkout-return?session_id={CHECKOUT_SESSION_ID}`}
              onCleared={async () => {
                setCheckoutPriceId(null);
                await qc.invalidateQueries({ queryKey: ["billing-status"] });
                navigate({ to: "/dashboard" });
              }}
            />
          </div>
        </div>
      )}
    </AppShell>
  );
}
